/**
 * View Details popup – open/close, focus trap, swatches. ADR-024.
 * Trigger: .currently-on-air-card__view-details or the card title (.currently-on-air-card__title-link)
 * Popup: .view-details-popup with id view-details-popup-*
 *
 * The popup markup is no longer server-rendered (it used to cost ~11.4 KB of the theme
 * extension's 100 KB Liquid budget for a modal that is never visible on first paint — see
 * docs/ASSET-SIZE-PLAN.md Step 6). It is now built here, on first open, from the
 * already-rendered on-air card: the buy form, reviews and highlights are CLONED from the
 * card rather than re-implemented, so none of their business logic (GLA/MLA, buy-all,
 * ring-resize, Budget Pay eligibility) is duplicated in JS. Only the static dialog shell
 * and the price/sku display are hand-built here — both pure presentation, no cart logic.
 *
 * This also means the popup now always opens with the card's CURRENT state (price, stock,
 * selected auction) instead of the page-load snapshot the old server-rendered popup carried
 * until the "sync description" workaround patched pieces of it back in — a class of bug this
 * rewrite removes rather than replicates.
 */
(function () {
  function initPopup(scope) {
    if (!scope) scope = document;
    /* The card title opens the popup too, instead of going to the PDP. */
    var triggers = scope.querySelectorAll('.currently-on-air-card__view-details[data-view-details-trigger], .currently-on-air-card__title-link[data-view-details-trigger]');
    triggers.forEach(function (link) {
      if (link._vdBound) return;
      link._vdBound = true;
      link.setAttribute('href', link.getAttribute('href') || '#');
      link.addEventListener('click', function (ev) {
        ev.preventDefault();
        var card = link.closest('.currently-on-air-card');
        var popup = card && buildPopupFromCard(card);
        if (popup) openPopup(popup, link);
      });
    });
  }

  /**
   * A form/select/input id collides with the card's own once both are in the document at
   * once (card stays in place; popup is appended to <body>). Every id inside `root` gets a
   * stable, unique suffix, and every `for` attribute pointing at one of those ids is updated
   * to match.
   *
   * The GLA/MLA config script is looked up as `form.id + "-gla-mla-config"`. Appending the
   * same suffix to both ids yields form-vd-...-gla-mla-config vs form-gla-mla-config-vd-..., so
   * the lookup misses and the second dropdown never rebuilds. realignGlaMlaConfigId puts
   * the script id back on that contract after the suffix pass.
   */
  function remapIds(root, suffix) {
    var renamed = {};
    var idEls = root.id ? [root] : [];
    idEls = idEls.concat(Array.prototype.slice.call(root.querySelectorAll('[id]')));
    idEls.forEach(function (el) {
      var oldId = el.id;
      var newId = oldId + suffix;
      renamed[oldId] = newId;
      el.id = newId;
    });
    var forEls = Array.prototype.slice.call(root.querySelectorAll('[for]'));
    forEls.forEach(function (el) {
      var target = renamed[el.getAttribute('for')];
      if (target) el.setAttribute('for', target);
    });
    realignGlaMlaConfigId(root);
  }

  function realignGlaMlaConfigId(root) {
    var form = root && root.tagName === 'FORM' ? root : root && root.querySelector ? root.querySelector('form') : null;
    if (!form || !form.id) return;
    var cfg = form.querySelector('script[type="application/json"][id*="gla-mla-config"]');
    if (cfg) cfg.id = form.id + '-gla-mla-config';
  }

  function copyGlaMlaConfigJson(fromForm, toForm) {
    if (!fromForm || !toForm) return;
    var src = fromForm.querySelector('script[type="application/json"][id*="gla-mla-config"]');
    var dest = toForm.querySelector('script[type="application/json"][id*="gla-mla-config"]');
    if (src && dest && src.textContent) dest.textContent = src.textContent;
  }

  function captureSelectKey(select) {
    if (!select || select.selectedIndex < 0) return null;
    var opt = select.options[select.selectedIndex];
    if (!opt) return null;
    var value = String(opt.value || '').trim();
    var sku = String(opt.getAttribute('data-sku') || opt.getAttribute('data-stock-code') || '').trim();
    var code = String(opt.getAttribute('data-auction-code') || '').trim();
    if ((!value || value === 'Select') && !sku && !code) return null;
    return {
      sku: sku,
      value: value,
      auctionCode: code
    };
  }

  function selectKeyAndNotify(select, key) {
    if (!select || !key) return false;
    var opts = select.options;
    var i;
    var opt;
    for (i = 0; i < opts.length; i++) {
      opt = opts[i];
      if (key.sku && (String(opt.getAttribute('data-sku') || '').trim() === key.sku || String(opt.getAttribute('data-stock-code') || '').trim() === key.sku)) {
        opt.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    for (i = 0; i < opts.length; i++) {
      opt = opts[i];
      if (key.auctionCode && String(opt.getAttribute('data-auction-code') || '').trim() === key.auctionCode) {
        opt.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    for (i = 0; i < opts.length; i++) {
      opt = opts[i];
      if (key.value && String(opt.value || '').trim() === key.value) {
        opt.selected = true;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        return true;
      }
    }
    return false;
  }

  function restoreGlaMlaFromCard(form, glaKey, mlaKey) {
    var glaSelect = form.querySelector('[data-gla-select]');
    var mlaSelect = form.querySelector('[data-mla-select]');
    if (glaSelect && glaKey) selectKeyAndNotify(glaSelect, glaKey);
    if (mlaSelect && mlaKey) selectKeyAndNotify(mlaSelect, mlaKey);
  }

  /**
   * Builds (or rebuilds) the popup for one card, freshly, every time it opens — cheaper than
   * it sounds, since everything but the gallery/price text is a cloneNode of markup the card
   * already rendered, and it means the popup can never show a stale selection from a previous
   * open. A previous instance for the same card is dropped rather than reused.
   */
  function buildPopupFromCard(card) {
    var popupId = card.getAttribute('data-view-details-popup-id');
    if (!popupId) return null;
    var existing = document.getElementById(popupId);
    if (existing && !existing.classList.contains('is-open')) existing.remove();
    else if (existing) return existing; // still open (e.g. re-triggered) — reuse as-is

    var titleEl = card.querySelector('.currently-on-air-card__title');
    var productTitle = titleEl ? titleEl.textContent.trim() : '';
    var productUrl = card.getAttribute('data-product-url') || '#';

    var popup = document.createElement('div');
    popup.className = 'view-details-popup';
    popup.id = popupId;
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', productTitle);
    popup.setAttribute('hidden', '');

    var iconUrls = readIconUrls(card);

    popup.innerHTML =
      '<div class="view-details-popup__backdrop" data-view-details-close aria-hidden="true"></div>' +
      '<div class="view-details-popup__dialog" data-view-details-dialog>' +
        '<button type="button" class="view-details-popup__close" data-view-details-close aria-label="Close" style="--close-icon-mask: url(' + iconUrls.close + ')">' +
          '<span class="view-details-popup__close-icon" aria-hidden="true"></span>' +
        '</button>' +
        '<div class="view-details-popup__body">' +
          '<div class="view-details-popup__gallery">' +
            '<div class="view-details-popup__main-image-wrap" data-view-details-main-wrap>' +
              '<img class="view-details-popup__main-image" data-view-details-main src="" alt="" width="400" height="360">' +
              '<button type="button" class="view-details-popup__fullscreen-btn" data-view-details-fullscreen data-close-icon-url="' + iconUrls.close + '" data-prev-icon-url="' + iconUrls.prev + '" data-next-icon-url="' + iconUrls.next + '" aria-label="Zoom">' +
                '<img src="' + iconUrls.expand + '" width="25" height="25" alt="" aria-hidden="true">' +
              '</button>' +
              '<button type="button" class="missed-auction-card__gallery-arrow missed-auction-card__gallery-arrow--prev" data-view-details-prev aria-label="Previous" hidden>' +
                '<img src="' + iconUrls.prev + '" alt="" width="24" height="24" aria-hidden="true">' +
              '</button>' +
              '<button type="button" class="missed-auction-card__gallery-arrow missed-auction-card__gallery-arrow--next" data-view-details-next aria-label="Next" hidden>' +
                '<img src="' + iconUrls.next + '" alt="" width="24" height="24" aria-hidden="true">' +
              '</button>' +
              '<span class="view-details-popup__zoom-pill">Hover to zoom</span>' +
            '</div>' +
            '<a href="' + productUrl + '" class="view-details-popup__view-full-details">' +
              'View full details' +
              '<span class="view-details-popup__link-chevron" aria-hidden="true"></span>' +
            '</a>' +
          '</div>' +
          '<div class="view-details-popup__content">' +
            '<div class="vdp_sku_slod_out" data-vd-sku-slot></div>' +
            '<h3 class="view-details-popup__product-title"></h3>' +
            '<div class="view-details-popup__rating"></div>' +
            '<div class="view-details-popup__price-block"><span class="view-details-popup__price"></span></div>' +
            '<div data-vd-form-slot></div>' +
            '<div data-vd-lowest-price-slot></div>' +
            '<div class="view-details-popup__highlights" data-vd-highlights-slot></div>' +
          '</div>' +
        '</div>' +
      '</div>';

    popup.querySelector('.view-details-popup__product-title').textContent = productTitle;
    copyCoaColours(popup, card);

    /* Attached (but still [hidden], so nothing flashes on screen) before populateForm runs:
       live-tv-card-forms.js locates the GLA/MLA config script via document.getElementById,
       which cannot see into a detached subtree the way querySelector can. */
    document.body.appendChild(popup);

    populateGallery(popup, card);
    populateSkuAndPrice(popup, card);
    populateRating(popup, card);
    populateForm(popup, card);
    populateLowestPrice(popup, card);
    populateHighlights(popup, card);

    return popup;
  }

  /* The popup lives on <body>, outside both the block wrapper that carries the editor's
     button colours (--coa-btn-*) and the card that defines the --coa-* tokens, so the cloned
     form would fall back to default colours. Copy the card's resolved values across. */
  var COA_COLOUR_VARS = [
    '--coa-btn-primary-bg', '--coa-btn-primary-text', '--coa-btn-hover-bg',
    '--coa-btn-quick-bg', '--coa-btn-quick-text', '--coa-btn-quick-hover-bg',
    '--coa-price-setting', '--coa-card-bg', '--coa-text', '--coa-muted', '--coa-accent',
    '--coa-price', '--coa-success', '--coa-border', '--coa-secondary-bg', '--coa-shop-pay'
  ];

  function copyCoaColours(popup, card) {
    var cs = window.getComputedStyle(card);
    for (var i = 0; i < COA_COLOUR_VARS.length; i++) {
      var value = cs.getPropertyValue(COA_COLOUR_VARS[i]).trim();
      if (value) popup.style.setProperty(COA_COLOUR_VARS[i], value);
    }
  }

  /** Asset URLs carry a Shopify CDN fingerprint JS cannot construct — the card exposes the
   *  handful the popup shell needs (see data-vd-icon-* on currently-on-air-card.liquid). */
  function readIconUrls(card) {
    return {
      close: card.getAttribute('data-vd-icon-close') || '',
      prev: card.getAttribute('data-vd-icon-prev') || '',
      next: card.getAttribute('data-vd-icon-next') || '',
      expand: card.getAttribute('data-vd-icon-expand') || '',
      noImage: card.getAttribute('data-vd-no-image') || ''
    };
  }

  function populateGallery(popup, card) {
    var mediaScript = card.querySelector('[data-view-details-media]');
    var urls = [];
    if (mediaScript) {
      try {
        var parsed = JSON.parse(mediaScript.textContent || '[]');
        if (Array.isArray(parsed)) urls = parsed.filter(Boolean);
      } catch (e) { /* malformed JSON — fall through to the no-image placeholder below */ }
    }
    var productTitle = popup.querySelector('.view-details-popup__product-title').textContent;
    if (urls.length > 0 && typeof window.updateViewDetailsGalleryFromMedia === 'function') {
      window.updateViewDetailsGalleryFromMedia(popup, urls, { productName: productTitle });
    } else {
      var mainImg = popup.querySelector('[data-view-details-main]');
      if (mainImg) {
        var iconUrls = readIconUrls(card);
        mainImg.src = iconUrls.noImage;
        mainImg.alt = productTitle;
      }
    }
  }

  function populateSkuAndPrice(popup, card) {
    var skuEl = card.querySelector('.currently-on-air-card__sku');
    var slot = popup.querySelector('[data-vd-sku-slot]');
    if (slot) {
      var sku = document.createElement('p');
      sku.className = 'view-details-popup__sku';
      sku.textContent = skuEl ? skuEl.textContent.trim() : '';
      slot.appendChild(sku);
      var soldItems = card.querySelector('.coa_sold_items');
      if (soldItems) slot.appendChild(soldItems.cloneNode(true));
    }
    var priceEl = card.querySelector('.currently-on-air-card__price');
    var popupPriceEl = popup.querySelector('.view-details-popup__price');
    if (priceEl && popupPriceEl) popupPriceEl.textContent = priceEl.textContent;

    /* Informational installments text only — the card's interactive Budget Pay toggle
       (its selling-plan <script> tags and the toggle button itself) is deliberately not
       cloned here: the popup has never offered that control, only the "or N payments of
       £X" line, and adding the live toggle is a behaviour change nobody asked for. */
    var cardInstallments = card.querySelector('.currently-on-air-card__installments');
    var bpWrap = card.querySelector('[data-ltv-budget-pay]');
    if (cardInstallments && bpWrap) {
      var priceBlock = popup.querySelector('.view-details-popup__price-block');
      var wrap = document.createElement('div');
      wrap.setAttribute('data-ltv-budget-pay', '');
      var installments = cardInstallments.cloneNode(true);
      installments.className = 'view-details-popup__installments';
      wrap.appendChild(installments);
      priceBlock.appendChild(wrap);
    }
  }

  function populateRating(popup, card) {
    var cardRating = card.querySelector('.currently-on-air-card__rating');
    var slot = popup.querySelector('.view-details-popup__rating');
    if (cardRating && slot) slot.innerHTML = cardRating.innerHTML;
  }

  function populateForm(popup, card) {
    var cardForm = card.querySelector('.currently-on-air-card__form');
    var slot = popup.querySelector('[data-vd-form-slot]');
    if (!cardForm || !slot) return;
    var glaKey = captureSelectKey(cardForm.querySelector('[data-gla-select]'));
    var mlaKey = captureSelectKey(cardForm.querySelector('[data-mla-select]'));
    var clone = cardForm.cloneNode(true);
    delete clone.dataset.coaFormInited;
    clone.removeAttribute('data-coa-form-inited');
    copyGlaMlaConfigJson(cardForm, clone);
    /* Matches the popup's historical include_simple_default_branch:false — a simple
       (non-group, non-MLA, non-ring-resize) auction shows quantity + Add to Basket only,
       with no separate quantity row, in the popup. */
    var simpleQtyRow = clone.querySelector('[data-simple-quantity-row]');
    if (simpleQtyRow) simpleQtyRow.remove();
    slot.replaceWith(clone);
    remapIds(clone, '-vd-' + (popup.id || 'popup'));
    if (typeof window.initCoaCardForms === 'function') {
      window.initCoaCardForms(clone);
    }
    restoreGlaMlaFromCard(clone, glaKey, mlaKey);
  }

  function populateLowestPrice(popup, card) {
    var cardLowestPrice = card.querySelector('.currently-on-air-card__lowest-price');
    var slot = popup.querySelector('[data-vd-lowest-price-slot]');
    if (cardLowestPrice && slot) slot.replaceWith(cardLowestPrice.cloneNode(true));
    else if (slot) slot.remove();
  }

  /**
   * The popup has always shown the full product description with no truncation
   * (live-tv-product-highlights.liquid's `expandable: false` branch), unlike the card's
   * "read more" accordion. Cloning the card's highlights and then discarding the
   * read-more wrapper (keeping only its text) reproduces that without a second
   * Liquid render of the same snippet.
   */
  function populateHighlights(popup, card) {
    var cardHighlights = card.querySelector('.currently-on-air-card__highlights');
    var slot = popup.querySelector('[data-vd-highlights-slot]');
    if (!cardHighlights || cardHighlights.hidden || !slot) { if (slot) slot.remove(); return; }
    var clone = cardHighlights.cloneNode(true);
    var wrapper = clone.querySelector('[data-coa-read-more-wrapper]');
    if (wrapper) {
      var content = wrapper.querySelector('[data-live-tv-highlights-about]');
      if (content) wrapper.replaceWith(content);
      else wrapper.remove();
    }
    slot.innerHTML = clone.innerHTML;
  }

  function openPopup(popup, returnFocusEl) {
    if (!popup) return;
    if (popup.parentNode !== document.body) {
      document.body.appendChild(popup);
    }
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    popup.removeAttribute('hidden');
    popup.style.display = 'flex';
    popup.classList.add('is-open');
    popup._vdReturnFocus = returnFocusEl || null;
    var dialog = popup.querySelector('[data-view-details-dialog]');
    var firstFocus = dialog && (dialog.querySelector('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled])') || dialog);
    if (firstFocus) firstFocus.focus({ preventScroll: true });
    initFocusTrap(popup);
    initSwatches(popup);
    initMainImageZoom(popup);
    initFullscreenBtn(popup);
  }
  function closePopup(popup) {
    if (!popup) return;
    popup.classList.remove('is-open');
    popup.setAttribute('hidden', '');
    popup.style.display = '';
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    var returnFocus = popup._vdReturnFocus;
    if (returnFocus && returnFocus.focus) returnFocus.focus({ preventScroll: true });
    popup._vdReturnFocus = null;
    if (popup._vdFocusTrapCleanup) popup._vdFocusTrapCleanup();
  }
  function initFocusTrap(popup) {
    var dialog = popup.querySelector('[data-view-details-dialog]');
    if (!dialog) return;
    var focusables = dialog.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])');
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    function onKey(e) {
      if (e.key !== 'Tab' && e.key !== 'Escape') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        closePopup(popup);
        return;
      }
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last && last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first && first.focus();
          }
        }
      }
    }
    dialog.addEventListener('keydown', onKey);
    popup._vdFocusTrapCleanup = function () {
      dialog.removeEventListener('keydown', onKey);
    };
  }
  /**
   * Sync main image + swatch rail from sku-data productMedia (MLA/GLA variant change).
   * Replaces placeholder div with <img> when needed; rebinds swatch clicks via initSwatches.
   */
  function updateViewDetailsGalleryFromMedia(popup, productMedia, opts) {
    opts = opts || {};
    if (!popup || !popup.classList || !popup.classList.contains('view-details-popup')) return;
    var urls = [];
    if (Array.isArray(productMedia)) {
      for (var mi = 0; mi < productMedia.length && urls.length < 8; mi++) {
        var m = productMedia[mi];
        var u = typeof m === 'string' ? m : (m && m.url);
        if (u) urls.push(String(u));
      }
    }
    if (urls.length === 0) return;
    var productName = opts.productName || '';
    var wrap = popup.querySelector('[data-view-details-main-wrap]');
    var mainEl = popup.querySelector('[data-view-details-main]');
    var galleryRoot = popup.querySelector('.view-details-popup__gallery');
    var primaryUrl = urls[0];
    if (mainEl) {
      if (mainEl.tagName === 'IMG') {
        mainImgSetSrc(mainEl, primaryUrl, productName);
      } else {
        var newImg = document.createElement('img');
        newImg.className = 'view-details-popup__main-image';
        newImg.setAttribute('data-view-details-main', '');
        mainImgSetSrc(newImg, primaryUrl, productName);
        newImg.width = 400;
        newImg.height = 360;
        mainEl.parentNode.replaceChild(newImg, mainEl);
      }
    }
    var rail = popup.querySelector('[data-view-details-swatches]');
    if (urls.length > 1) {
      if (!rail && galleryRoot) {
        rail = document.createElement('div');
        rail.className = 'view-details-popup__swatches';
        rail.setAttribute('data-view-details-swatches', '');
        rail.setAttribute('role', 'list');
        var viewFull = galleryRoot.querySelector('.view-details-popup__view-full-details');
        if (viewFull) galleryRoot.insertBefore(rail, viewFull);
        else galleryRoot.appendChild(rail);
      }
      if (rail) {
        var sb = [];
        for (var j = 0; j < urls.length; j++) {
          var fs = urls[j].replace(/"/g, '&quot;');
          var act = j === 0 ? ' is-active' : '';
          sb.push('<button type="button" class="view-details-popup__swatch' + act + '" data-view-details-swatch data-view-details-full-src="' + fs + '" data-index="' + j + '" role="listitem" aria-label="Image ' + (j + 1) + '">');
          sb.push('<img src="' + fs + '" alt="" width="62" height="62" loading="lazy">');
          sb.push('</button>');
        }
        rail.innerHTML = sb.join('');
      }
      toggleGalleryArrows(popup, true);
    } else {
      if (rail) rail.remove();
      toggleGalleryArrows(popup, false);
    }
    initSwatches(popup);
    if (wrap && !wrap._vdZoomBound) initMainImageZoom(popup);
  }

  function mainImgSetSrc(img, src, altText) {
    img.src = src;
    img.removeAttribute('srcset');
    if (altText) img.alt = altText;
  }

  function toggleGalleryArrows(popup, show) {
    var prev = popup.querySelector('[data-view-details-prev]');
    var next = popup.querySelector('[data-view-details-next]');
    if (prev) prev.hidden = !show;
    if (next) next.hidden = !show;
  }

  function initSwatches(popup) {
    var mainImg = popup.querySelector('[data-view-details-main]');
    var swatches = popup.querySelectorAll('[data-view-details-swatch]');
    if (!mainImg || mainImg.tagName !== 'IMG' || !swatches.length) return;
    var prevBtn = popup.querySelector('[data-view-details-prev]');
    var nextBtn = popup.querySelector('[data-view-details-next]');

    /** Re-query DOM so prev/next still work after MLA/GLA rebuilds the swatch rail. */
    function currentSwatches() {
      return popup.querySelectorAll('[data-view-details-swatch]');
    }

    function getActiveIndex(sw) {
      var idx = 0;
      sw.forEach(function (s, i) {
        if (s.classList.contains('is-active')) idx = i;
      });
      return idx;
    }

    function activateAt(index) {
      var sw = currentSwatches();
      if (!sw.length) return;
      var total = sw.length;
      var normalized = ((index % total) + total) % total;
      var btn = sw[normalized];
      if (!btn) return;
      var fullSrc = btn.getAttribute('data-view-details-full-src');
      var thumb = btn.querySelector('img');
      if (fullSrc) {
        mainImg.src = fullSrc;
      } else if (thumb && thumb.src) {
        mainImg.src = thumb.src;
      }
      mainImg.srcset = '';
      sw.forEach(function (s) { s.classList.remove('is-active'); });
      btn.classList.add('is-active');
      var rail = popup.querySelector('[data-view-details-swatches]');
      if (rail && typeof btn.scrollIntoView === 'function') {
        btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    swatches.forEach(function (btn) {
      btn.addEventListener('click', function () {
        activateAt(Array.prototype.indexOf.call(currentSwatches(), btn));
      });
    });

    if (prevBtn && !prevBtn._vdBound) {
      prevBtn._vdBound = true;
      prevBtn.addEventListener('click', function () {
        var sw = currentSwatches();
        activateAt(getActiveIndex(sw) - 1);
      });
    }
    if (nextBtn && !nextBtn._vdBound) {
      nextBtn._vdBound = true;
      nextBtn.addEventListener('click', function () {
        var sw = currentSwatches();
        activateAt(getActiveIndex(sw) + 1);
      });
    }
  }
  /**
   * Cursor-based "hover zoom": transform-origin follows pointer so the magnified area matches expectation.
   * Only binds for real <img> main images (not placeholder divs).
   */
  function initMainImageZoom(popup) {
    var wrap = popup && popup.querySelector('[data-view-details-main-wrap]');
    if (!wrap || wrap._vdZoomBound) return;
    var img = wrap.querySelector('.view-details-popup__main-image');
    if (!img || img.tagName !== 'IMG') return;
    wrap._vdZoomBound = true;
    wrap.style.setProperty('--vd-zoom-x', '50%');
    wrap.style.setProperty('--vd-zoom-y', '50%');
    wrap.addEventListener('mousemove', function (e) {
      var rect = wrap.getBoundingClientRect();
      var w = rect.width;
      var h = rect.height;
      if (w < 1 || h < 1) return;
      var x = ((e.clientX - rect.left) / w) * 100;
      var y = ((e.clientY - rect.top) / h) * 100;
      wrap.style.setProperty('--vd-zoom-x', Math.max(0, Math.min(100, x)) + '%');
      wrap.style.setProperty('--vd-zoom-y', Math.max(0, Math.min(100, y)) + '%');
    });
    wrap.addEventListener('mouseleave', function () {
      wrap.style.setProperty('--vd-zoom-x', '50%');
      wrap.style.setProperty('--vd-zoom-y', '50%');
    });
  }
  /** Singleton div overlay for image zoom. Built once, reused across popups. */
  var _vdZoomDlg = null;

  function closeZoomDialog() {
    if (_vdZoomDlg) _vdZoomDlg.classList.remove('is-open');
  }

  function getZoomDialog(iconUrls) {
    if (_vdZoomDlg) return _vdZoomDlg;
    var el = document.createElement('div');
    el.className = 'vd-zoom-dialog';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Full screen image');
    el.innerHTML =
      '<button class="vd-zoom-dialog__close" data-vd-zoom-close aria-label="Close">' +
        '<img src="' + (iconUrls.close || '') + '" width="24" height="24" alt="" aria-hidden="true">' +
      '</button>' +
      '<div class="vd-zoom-dialog__frame">' +
        '<img class="vd-zoom-dialog__img" src="" alt="" width="1200" height="1200">' +
      '</div>' +
      '<button class="vd-zoom-dialog__arrow vd-zoom-dialog__arrow--prev" data-vd-zoom-prev aria-label="Previous image" hidden>' +
        '<img src="' + (iconUrls.prev || '') + '" width="24" height="24" alt="" aria-hidden="true">' +
      '</button>' +
      '<button class="vd-zoom-dialog__arrow vd-zoom-dialog__arrow--next" data-vd-zoom-next aria-label="Next image" hidden>' +
        '<img src="' + (iconUrls.next || '') + '" width="24" height="24" alt="" aria-hidden="true">' +
      '</button>';
    document.body.appendChild(el);
    el.querySelector('[data-vd-zoom-close]').addEventListener('click', closeZoomDialog);
    el.addEventListener('click', function (e) { if (e.target === el) closeZoomDialog(); });
    _vdZoomDlg = el;
    return el;
  }

  function openZoomDialog(popup, triggerBtn) {
    var iconUrls = {
      close: triggerBtn.getAttribute('data-close-icon-url') || '',
      prev: triggerBtn.getAttribute('data-prev-icon-url') || '',
      next: triggerBtn.getAttribute('data-next-icon-url') || ''
    };
    var dlg = getZoomDialog(iconUrls);
    var dlgImg = dlg.querySelector('.vd-zoom-dialog__img');

    // Collect full-res URLs from swatches; fall back to the active main image src.
    var swatches = Array.prototype.slice.call(popup.querySelectorAll('[data-view-details-swatch]'));
    var urls = [];
    swatches.forEach(function (s) {
      var src = s.getAttribute('data-view-details-full-src');
      if (src) urls.push(src);
    });
    if (urls.length === 0) {
      var mainImg = popup.querySelector('[data-view-details-main]');
      if (mainImg && mainImg.tagName === 'IMG' && mainImg.src) urls.push(mainImg.src);
    }
    if (urls.length === 0) return;

    // Start at the currently-active swatch.
    var currentIdx = 0;
    swatches.forEach(function (s, i) { if (s.classList.contains('is-active')) currentIdx = i; });

    // Re-clone prev/next to strip leftover listeners from a prior session.
    var oldPrev = dlg.querySelector('[data-vd-zoom-prev]');
    var oldNext = dlg.querySelector('[data-vd-zoom-next]');
    var newPrev = oldPrev.cloneNode(true);
    var newNext = oldNext.cloneNode(true);
    oldPrev.parentNode.replaceChild(newPrev, oldPrev);
    oldNext.parentNode.replaceChild(newNext, oldNext);

    function showAt(idx) {
      currentIdx = ((idx % urls.length) + urls.length) % urls.length;
      dlgImg.src = urls[currentIdx];
      var altEl = popup.querySelector('[data-view-details-main]');
      dlgImg.alt = altEl ? altEl.alt : '';
      newPrev.hidden = urls.length < 2;
      newNext.hidden = urls.length < 2;
    }

    newPrev.addEventListener('click', function () { showAt(currentIdx - 1); });
    newNext.addEventListener('click', function () { showAt(currentIdx + 1); });

    // Replace arrow-key handler so it captures the fresh closures above.
    if (dlg._vdOnKey) dlg.removeEventListener('keydown', dlg._vdOnKey);
    dlg._vdOnKey = function (e) {
      if (e.key === 'ArrowLeft') { e.preventDefault(); showAt(currentIdx - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); showAt(currentIdx + 1); }
      else if (e.key === 'Escape') { e.stopPropagation(); closeZoomDialog(); }
    };
    dlg.addEventListener('keydown', dlg._vdOnKey);

    showAt(currentIdx);
    dlg.classList.add('is-open');
  }

  function initFullscreenBtn(popup) {
    var btn = popup.querySelector('[data-view-details-fullscreen]');
    if (!btn || btn._vdFsBound) return;
    btn._vdFsBound = true;
    btn.addEventListener('click', function () { openZoomDialog(popup, btn); });
  }

  document.addEventListener('click', function (ev) {
    var closeBtn = ev.target.closest('[data-view-details-close]');
    if (closeBtn) {
      var popup = closeBtn.closest('.view-details-popup');
      if (popup) {
        ev.preventDefault();
        closePopup(popup);
      }
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (_vdZoomDlg && _vdZoomDlg.classList.contains('is-open')) return;
    var openPopupEl = document.querySelector('.view-details-popup.is-open');
    if (openPopupEl) closePopup(openPopupEl);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initPopup(); });
  } else {
    initPopup();
  }
  window.initViewDetailsPopups = initPopup;
  window.updateViewDetailsGalleryFromMedia = updateViewDetailsGalleryFromMedia;
})();
